import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PssAuthService, PssSession, PssPlayerDetails, PssFleetMessage } from '../../../services/pss-auth.service';
import { FeatureFlagService, FEATURE_FLAGS } from '../../../services/feature-flag.service';

@Component({
  selector: 'app-fleet-chat-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fleet-chat-tab.component.html',
  styleUrls: ['./fleet-chat-tab.component.css']
})
export class FleetChatTabComponent implements OnInit {
  @Input() session: PssSession | null = null;
  @Input() details: PssPlayerDetails | null = null;

  @ViewChild('chatMessages') chatMessagesEl?: ElementRef<HTMLElement>;

  fleetMessages: PssFleetMessage[] = [];
  fleetChatLoading = false;
  fleetChatInput = '';
  fleetChatSending = false;
  fleetChatError = '';
  // True when the most recent successful load came from the stored history
  // endpoint. Exposed for the UI to show a small badge so the user knows
  // they're seeing long-range history vs. just the live PSS window.
  usingStoredHistory = false;
  // Donation/automation system messages tend to dominate alliance chat;
  // unchecking hides them so the player can see real conversation.
  showDonations = false;

  // Patterns the game emits for automated alliance-wide notifications.
  // Matched case-insensitively against message body.
  private static readonly DONATION_NOISE_RE =
    /has borrowed character|has extended donation time|has donated/i;

  get basicPlus(): boolean { return this.featureFlags.isBasicPlus(); }
  get restrictedMode(): boolean { return this.featureFlags.isRestrictedMode(); }

  get filteredMessages(): PssFleetMessage[] {
    if (this.showDonations) return this.fleetMessages;
    return this.fleetMessages.filter(m =>
      !FleetChatTabComponent.DONATION_NOISE_RE.test(m.message || '')
    );
  }

  constructor(private auth: PssAuthService, private router: Router, private featureFlags: FeatureFlagService) {}

  ngOnInit(): void {
    // Load once on tab open. No auto-poll: refreshing the session token while
    // the player is in the game would kick them out, so chat only refreshes on
    // user action (tab change re-mounts this component, or the refresh button).
    this.loadFleetMessages();
  }

  loadFleetMessages(): void {
    if (!this.session || !this.details?.allianceId) return;
    const allianceId = this.details.allianceId;
    this.fleetChatLoading = true;
    this.fleetChatError = '';

    // Prefer the stored history (populated by the fleet_chat polling task).
    // If the alliance isn't being polled the endpoint returns an empty array
    // and we transparently fall back to the live PSS API.
    this.auth.getStoredFleetMessages(allianceId).subscribe({
      next: stored => {
        if (stored.length > 0) {
          this.fleetMessages = stored;
          this.usingStoredHistory = true;
          this.fleetChatLoading = false;
          setTimeout(() => this.scrollChatToBottom(), 0);
          return;
        }
        this.loadLiveFleetMessages();
      },
      error: () => this.loadLiveFleetMessages(),
    });
  }

  private loadLiveFleetMessages(isRetry = false): void {
    if (!this.session || !this.details?.allianceId) {
      this.fleetChatLoading = false;
      return;
    }
    this.usingStoredHistory = false;
    this.auth.getFleetMessages(
      this.session.productionServer,
      this.details.allianceId,
      this.session.accessToken
    ).subscribe({
      next: msgs => {
        this.fleetMessages = msgs;
        this.fleetChatLoading = false;
        setTimeout(() => this.scrollChatToBottom(), 0);
      },
      error: () => {
        if (!isRetry) { this.reauthAndRun(() => this.loadLiveFleetMessages(true)); return; }
        this.fleetChatError = 'Could not load chat messages.';
        this.fleetChatLoading = false;
      }
    });
  }

  sendFleetChat(): void {
    if (this.restrictedMode) return;
    if (!this.fleetChatInput.trim() || this.fleetChatSending || !this.details?.allianceId) return;
    this.fleetChatSending = true;
    this.fleetChatError = '';
    this.auth.sendFleetMessage(this.details.allianceId, this.fleetChatInput.trim()).subscribe({
      next: () => {
        this.fleetChatInput = '';
        this.fleetChatSending = false;
        // Fetch from live so the user's just-sent message shows up immediately —
        // the stored history won't reflect it until the next polling tick (~60s).
        this.loadLiveFleetMessages();
      },
      error: err => {
        this.fleetChatError = err?.error?.error || 'Failed to send message';
        this.fleetChatSending = false;
      }
    });
  }

  formatChatTime(iso: string): string {
    if (!iso || iso.startsWith('2000-01-01')) return '';
    const d = new Date(iso.includes('T') ? iso + (iso.endsWith('Z') ? '' : 'Z') : iso + 'T00:00:00Z');
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  private scrollChatToBottom(): void {
    if (this.chatMessagesEl) {
      const el = this.chatMessagesEl.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  private reauthAndRun(action: () => void): void {
    this.auth.reauthenticate().subscribe({
      next: newSession => {
        this.session = newSession;
        action();
      },
      error: () => this.router.navigate(['/'])
    });
  }
}
